import { useState, useRef } from 'react';
import { useWalletConnect } from '@btc-vision/walletconnect';
import { getContract, JSONRpcProvider } from 'opnet';
import { networks } from '@btc-vision/bitcoin';
import { CATEGORIES } from '../config/campaigns';
import { BITFUND_CONTRACT_ADDRESS, BITFUND_ABI, IBitFundContract } from '../config/contract';

/**
 * Converts a BTC string amount to satoshis using string parsing.
 * Avoids float multiplication which loses precision.
 */
function btcStringToSats(btcStr: string): bigint {
    const trimmed = btcStr.trim();
    if (!trimmed || trimmed === '0') return 0n;

    const dotIndex = trimmed.indexOf('.');
    if (dotIndex === -1) {
        return BigInt(trimmed) * 100_000_000n;
    }

    const whole = trimmed.slice(0, dotIndex) || '0';
    const frac = trimmed.slice(dotIndex + 1).padEnd(8, '0').slice(0, 8);
    return BigInt(whole) * 100_000_000n + BigInt(frac);
}

interface CreateCampaignModalProps {
    readonly onClose: () => void;
    readonly onSuccess: () => void;
}

export function CreateCampaignModal({ onClose, onSuccess }: CreateCampaignModalProps) {
    const { walletAddress, openConnectModal, provider, address } = useWalletConnect();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<string>(CATEGORIES[0].value);
    const [goal, setGoal] = useState('');
    const [duration, setDuration] = useState('30');
    const [step, setStep] = useState<'form' | 'sending' | 'success'>('form');
    const [createdTitle, setCreatedTitle] = useState('');
    const [error, setError] = useState('');
    const submittingRef = useRef(false);

    const selectedCat = CATEGORIES.find((c) => c.value === category);
    const categoryIndex = CATEGORIES.findIndex((c) => c.value === category);
    const isValid =
        title.trim().length >= 3 &&
        description.trim().length >= 10 &&
        parseFloat(goal) > 0 &&
        parseInt(duration) > 0 &&
        parseInt(duration) <= 90;

    const handleCreate = async () => {
        if (submittingRef.current) return;
        setError('');

        if (!walletAddress || !provider || !address) {
            setError('Wallet not connected. Please connect OP_WALLET first.');
            return;
        }

        if (title.trim().length < 3) {
            setError('Title must be at least 3 characters.');
            return;
        }
        if (title.trim().length > 80) {
            setError('Title must be at most 80 characters.');
            return;
        }
        if (description.trim().length < 10) {
            setError('Description must be at least 10 characters.');
            return;
        }
        if (description.trim().length > 2000) {
            setError('Description must be at most 2000 characters.');
            return;
        }

        const goalValue = parseFloat(goal);
        if (!goalValue || goalValue <= 0) {
            setError('Please enter a valid BTC goal.');
            return;
        }

        const durationValue = parseInt(duration);
        if (!durationValue || durationValue < 1 || durationValue > 90) {
            setError('Duration must be 1-90 days.');
            return;
        }

        submittingRef.current = true;
        setStep('sending');
        setError('');

        try {
            const rpcProvider = provider as JSONRpcProvider;
            const network = networks.opnetTestnet;

            const contract = getContract<IBitFundContract>(
                BITFUND_CONTRACT_ADDRESS,
                BITFUND_ABI,
                rpcProvider,
                network,
                address,
            );

            const goalSats = btcStringToSats(goal);

            // Simulate createCampaign — no BTC output needed (state write only)
            const sim = await contract.createCampaign(
                title.trim(),
                description.trim(),
                '', // metadata — reserved for future use (links, images, etc.)
                goalSats,
                durationValue,
                categoryIndex,
            );

            if ('error' in sim && sim.error) {
                throw new Error(String(sim.error));
            }

            // Send transaction — signer: null on frontend (wallet handles signing)
            const receipt = await sim.sendTransaction({
                signer: null,
                mldsaSigner: null,
                refundTo: walletAddress,
                maximumAllowedSatToSpend: 100_000n,
                network,
            });

            if (!receipt.transactionId) {
                throw new Error('Transaction returned no result');
            }

            setCreatedTitle(title.trim());
            setStep('success');
        } catch (err) {
            const msg =
                err instanceof Error ? err.message : String(err);
            setError('Failed: ' + msg);
            setStep('form');
        } finally {
            submittingRef.current = false;
        }
    };

    const handleDone = () => {
        onSuccess();
        setTimeout(() => {
            document.getElementById('campaigns')?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && step === 'form') onClose();
    };

    // ─── SUCCESS SCREEN ───
    if (step === 'success') {
        return (
            <div className="modal-overlay" onClick={handleOverlayClick}>
                <div className="modal" style={{ maxWidth: 560 }}>
                    <div className="modal__header">
                        <h2 className="modal__title">Campaign Created!</h2>
                    </div>
                    <div style={{ textAlign: 'center', padding: 'var(--space-xl) 0' }}>
                        <div
                            style={{
                                fontSize: '4rem',
                                marginBottom: 'var(--space-lg)',
                            }}
                        >
                            {'\u2705'}
                        </div>
                        <p
                            style={{
                                fontSize: 'var(--font-size-lg)',
                                color: 'var(--text-primary)',
                                marginBottom: 'var(--space-sm)',
                            }}
                        >
                            Your campaign is live on-chain!
                        </p>
                        <p
                            style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--text-secondary)',
                                marginBottom: 'var(--space-md)',
                            }}
                        >
                            &quot;{createdTitle}&quot; &mdash; {goal} BTC goal &middot; {duration}{' '}
                            days
                        </p>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-sm)',
                                justifyContent: 'center',
                                padding: 'var(--space-sm) var(--space-md)',
                                background: 'rgba(251, 191, 36, 0.08)',
                                border: '1px solid rgba(251, 191, 36, 0.2)',
                                borderRadius: 'var(--radius-md)',
                                marginBottom: 'var(--space-xl)',
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-warm)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <p
                                style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--accent-warm)',
                                    lineHeight: 1.4,
                                }}
                            >
                                Your campaign will appear on the page after the next block confirmation (~10 min on testnet).
                            </p>
                        </div>
                        <button
                            className="btn btn--primary btn--lg"
                            style={{ width: '100%' }}
                            onClick={handleDone}
                        >
                            View My Campaign
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── CONNECT WALLET SCREEN ───
    if (!walletAddress) {
        return (
            <div className="modal-overlay" onClick={handleOverlayClick}>
                <div className="modal" style={{ maxWidth: 560 }}>
                    <div className="modal__header">
                        <h2 className="modal__title">Create Campaign</h2>
                        <button className="modal__close" onClick={onClose} aria-label="Close">
                            x
                        </button>
                    </div>
                    <div style={{ textAlign: 'center', padding: 'var(--space-2xl) 0' }}>
                        <div style={{ fontSize: '3rem', marginBottom: 'var(--space-lg)' }}>
                            {'\u{1F4F2}'}
                        </div>
                        <p
                            style={{
                                fontSize: 'var(--font-size-lg)',
                                color: 'var(--text-primary)',
                                marginBottom: 'var(--space-sm)',
                            }}
                        >
                            Connect Your Wallet
                        </p>
                        <p
                            style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--text-secondary)',
                                marginBottom: 'var(--space-xl)',
                                maxWidth: '36ch',
                                margin: '0 auto var(--space-xl)',
                            }}
                        >
                            You need OP_WALLET connected to create a campaign. Your wallet address
                            becomes the campaign&apos;s receiving address.
                        </p>
                        <button className="btn btn--primary btn--lg" onClick={openConnectModal}>
                            Connect OP_WALLET
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── FORM ───
    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal" style={{ maxWidth: 560 }}>
                <div className="modal__header">
                    <h2 className="modal__title">Create Campaign</h2>
                    <button
                        className="modal__close"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={step === 'sending'}
                    >
                        x
                    </button>
                </div>

                <div className="input-group">
                    <label className="input-group__label" htmlFor="campaign-title">
                        Campaign Title
                    </label>
                    <input
                        id="campaign-title"
                        className="input-group__field"
                        style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--font-size-base)',
                        }}
                        type="text"
                        placeholder="e.g. Bitcoin Education for Schools"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={80}
                        disabled={step === 'sending'}
                    />
                    <span
                        style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-muted)',
                            marginTop: 4,
                            display: 'block',
                        }}
                    >
                        {title.length}/80
                    </span>
                </div>

                <div className="input-group">
                    <label className="input-group__label" htmlFor="campaign-desc">
                        Description
                    </label>
                    <textarea
                        id="campaign-desc"
                        className="input-group__field"
                        style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--font-size-sm)',
                            minHeight: 100,
                            resize: 'vertical',
                        }}
                        placeholder="Describe your campaign - what's the goal, why does it matter, how will the funds be used?"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={2000}
                        disabled={step === 'sending'}
                    />
                    <span
                        style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-muted)',
                            marginTop: 4,
                            display: 'block',
                        }}
                    >
                        {description.length}/2000
                    </span>
                </div>

                <div className="input-group">
                    <label className="input-group__label">Category</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.value}
                                className="input-group__preset"
                                style={{
                                    borderColor:
                                        category === cat.value
                                            ? 'var(--accent-primary)'
                                            : undefined,
                                    color:
                                        category === cat.value
                                            ? 'var(--accent-primary)'
                                            : undefined,
                                    background:
                                        category === cat.value
                                            ? 'var(--accent-glow)'
                                            : undefined,
                                    fontSize: 'var(--font-size-xs)',
                                    fontFamily: 'var(--font-body)',
                                }}
                                onClick={() => setCategory(cat.value)}
                                type="button"
                                disabled={step === 'sending'}
                            >
                                {cat.icon} {cat.value}
                            </button>
                        ))}
                    </div>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--space-md)',
                    }}
                >
                    <div className="input-group">
                        <label className="input-group__label" htmlFor="campaign-goal">
                            Goal (BTC)
                        </label>
                        <input
                            id="campaign-goal"
                            className="input-group__field"
                            type="number"
                            step="0.01"
                            min="0.001"
                            placeholder="1.0"
                            value={goal}
                            onChange={(e) => setGoal(e.target.value)}
                            disabled={step === 'sending'}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-group__label" htmlFor="campaign-duration">
                            Duration (days)
                        </label>
                        <input
                            id="campaign-duration"
                            className="input-group__field"
                            type="number"
                            min="1"
                            max="90"
                            placeholder="30"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            disabled={step === 'sending'}
                        />
                    </div>
                </div>

                <div className="modal__summary" style={{ marginTop: 'var(--space-sm)' }}>
                    <span className="modal__summary-label">Receiving Address</span>
                    <span
                        className="modal__summary-value"
                        style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                        {walletAddress.slice(0, 12)}...{walletAddress.slice(-6)}
                    </span>
                </div>

                {error && (
                    <p
                        style={{
                            color: 'var(--color-error)',
                            fontSize: 'var(--font-size-sm)',
                            textAlign: 'center',
                            marginTop: 'var(--space-md)',
                            padding: 'var(--space-sm)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: 'var(--radius-md)',
                        }}
                    >
                        {error}
                    </p>
                )}

                <button
                    className="btn btn--primary btn--lg"
                    style={{ width: '100%', marginTop: 'var(--space-md)' }}
                    onClick={handleCreate}
                    disabled={!isValid || step === 'sending'}
                >
                    {step === 'sending' ? 'Confirm in OP_WALLET...' : 'Create Campaign'}
                </button>

                {step === 'form' && !isValid && (
                    <p
                        style={{
                            color: 'var(--text-muted)',
                            fontSize: 'var(--font-size-xs)',
                            textAlign: 'center',
                            marginTop: 'var(--space-sm)',
                        }}
                    >
                        Fill in all fields: title (3+ chars), description (10+ chars), BTC goal, and
                        duration (1-90 days).
                    </p>
                )}
            </div>
        </div>
    );
}
